import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CONV_KEY = "pharma360_chat_conv";
const ADVISOR_AVATAR = "https://static.prod-images.emergentagent.com/jobs/2a338cad-441d-4dda-a18b-505c5d5d8792/images/eb2b26c2cb3e797c665ea1348c6501d87779ebd6b2be48fa1e96b5c269a97a3e.jpeg";

export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [convId, setConvId] = useState(() => localStorage.getItem(CONV_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [form, setForm] = useState({ name: "", email: "" });
  const [starting, setStarting] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const loadMessages = async (id) => {
    try { const { data } = await api.get(`/chat/${id}/messages`); setMessages(data); }
    catch (e) {
      // Only forget the conversation if it truly no longer exists (404).
      // Transient errors (network/5xx) must NOT wipe the visible messages.
      if (e?.response?.status === 404) { localStorage.removeItem(CONV_KEY); setConvId(""); setMessages([]); }
    }
  };

  useEffect(() => {
    if (open && convId) {
      loadMessages(convId);
      pollRef.current = setInterval(() => loadMessages(convId), 4000);
      return () => clearInterval(pollRef.current);
    }
  }, [open, convId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const startConversation = async () => {
    const { data } = await api.post("/chat/start", { name: form.name, email: form.email });
    localStorage.setItem(CONV_KEY, data.id); setConvId(data.id);
    return data.id;
  };

  const start = async () => {
    setStarting(true);
    try { await startConversation(); setMessages([]); }
    catch {} finally { setStarting(false); }
  };

  // Post to the current conversation; if it no longer exists (404), transparently
  // recreate a fresh conversation and retry so the message is never lost.
  const postToConversation = async (payload) => {
    let id = convId;
    try {
      await api.post(`/chat/${id}/message`, payload);
      return id;
    } catch (e) {
      if (e?.response?.status === 404) {
        localStorage.removeItem(CONV_KEY);
        id = await startConversation();
        await api.post(`/chat/${id}/message`, payload);
        return id;
      }
      throw e;
    }
  };

  const send = async () => {
    const t = text.trim(); if (!t || !convId) return;
    setText("");
    const tmpId = `tmp${Date.now()}`;
    setMessages((m) => [...m, { id: tmpId, sender: "user", text: t }]);
    try {
      const id = await postToConversation({ text: t });
      await loadMessages(id);
    } catch {
      toast.error("Message non envoyé, veuillez réessayer.");
    }
  };

  const sendImage = async (file) => {
    if (!file || !convId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const id = await postToConversation({ image: data.url });
      await loadMessages(id);
    } catch { toast.error("Image non envoyée, veuillez réessayer."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const needsStart = !convId && !user;

  return (
    <>
      {!open && (
        <div className="fixed bottom-5 right-5 z-[9998] flex items-center gap-3">
          <button onClick={() => setOpen(true)} data-testid="chat-help-label"
            className="hidden sm:block bg-white shadow-lg rounded-2xl px-4 py-2.5 text-sm font-semibold text-slate-700 border border-mint-100 animate-pulse-slow hover:text-mint-700">
            Besoin d'aide ? <span className="text-mint-600">Cliquez ici</span>
          </button>
          <button onClick={() => setOpen(true)} data-testid="chat-open-btn" aria-label="Ouvrir le chat"
            className="relative w-16 h-16 rounded-full bg-white shadow-xl shadow-mint-600/30 grid place-items-center transition-transform hover:scale-105 ring-4 ring-mint-500/30">
            <img src={ADVISOR_AVATAR} alt="Conseillère" className="w-14 h-14 rounded-full object-cover" />
            <span className="absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full bg-green-500 border-2 border-white" />
            <span className="absolute -top-1 -left-1 w-6 h-6 rounded-full bg-mint-600 text-white grid place-items-center shadow"><MessageCircle className="w-3.5 h-3.5" /></span>
          </button>
        </div>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-[9999] w-[92vw] max-w-sm bg-white rounded-3xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="chat-window" style={{ height: "min(70vh, 520px)" }}>
          <div className="bg-mint-600 text-white px-5 py-4 flex items-center gap-3">
            <img src={ADVISOR_AVATAR} alt="Conseillère" className="w-11 h-11 rounded-full object-cover ring-2 ring-white/40 shrink-0" />
            <div className="flex-1">
              <div className="font-display font-bold">Service client Pharma360</div>
              <div className="text-xs text-mint-100 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-400" /> En ligne · réponse rapide</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-btn" className="hover:bg-white/15 rounded-full p-1"><X className="w-5 h-5" /></button>
          </div>

          {needsStart ? (
            <div className="flex-1 p-5 flex flex-col justify-center gap-3" data-testid="chat-start-form">
              <p className="text-sm text-slate-600">Bonjour ! Laissez-nous vos coordonnées pour démarrer la discussion.</p>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Votre nom" data-testid="chat-name" className="w-full px-4 py-2.5 rounded-xl border border-mint-200 outline-none focus:ring-2 focus:ring-mint-500 text-sm" />
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optionnel)" data-testid="chat-email" className="w-full px-4 py-2.5 rounded-xl border border-mint-200 outline-none focus:ring-2 focus:ring-mint-500 text-sm" />
              <button onClick={start} disabled={starting || !form.name.trim()} data-testid="chat-start-btn" className="py-2.5 rounded-full bg-mint-600 text-white font-semibold text-sm disabled:opacity-50">{starting ? "…" : "Démarrer la discussion"}</button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-auto p-4 space-y-3 bg-mint-50/30" data-testid="chat-messages">
                {(!convId && user) && (
                  <div className="text-center">
                    <button onClick={start} disabled={starting} data-testid="chat-start-btn" className="px-4 py-2 rounded-full bg-mint-600 text-white font-semibold text-sm">{starting ? "…" : "Démarrer la discussion"}</button>
                  </div>
                )}
                {messages.length === 0 && convId && <p className="text-center text-sm text-slate-400 mt-6">Envoyez votre premier message !</p>}
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-1 py-1 rounded-2xl text-sm overflow-hidden ${m.sender === "user" ? "bg-mint-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
                      {m.image && <a href={mediaUrl(m.image)} target="_blank" rel="noopener noreferrer"><img src={mediaUrl(m.image)} alt="pièce jointe" className="rounded-xl max-h-48 w-auto object-cover mb-1" data-testid="chat-message-image" /></a>}
                      {m.text && <div className="px-3 py-1.5">{m.text}</div>}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              {convId && (
                <div className="p-3 border-t border-slate-100 flex items-center gap-2">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="chat-image-input" onChange={(e) => sendImage(e.target.files?.[0])} />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="chat-image-btn" aria-label="Envoyer une image" className="w-10 h-10 rounded-full border border-mint-200 text-mint-700 grid place-items-center shrink-0 hover:bg-mint-50 disabled:opacity-50"><ImagePlus className="w-4.5 h-4.5" /></button>
                  <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={uploading ? "Envoi de l'image…" : "Écrivez un message…"} data-testid="chat-input" className="flex-1 px-4 py-2.5 rounded-full border border-mint-200 outline-none focus:ring-2 focus:ring-mint-500 text-sm" />
                  <button onClick={send} data-testid="chat-send-btn" className="w-10 h-10 rounded-full bg-mint-600 text-white grid place-items-center shrink-0"><Send className="w-4 h-4" /></button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
